/**
 * Payment-failure banner (Sheila directive, 2026-08-06): shown on the
 * family + weekly-orders pages from the FIRST failed payment on the
 * household (derived from `/api/family/me`'s `paymentFailure`), escalating
 * its copy once the 13-day ordering lockout is in effect. The CTA sends the
 * parent to Billing — updating the card or topping up clears the failure
 * and the banner (and any lockout) disappears on the next load.
 */

import { Button } from '@ordo/ui/button'
import { useNavigate } from 'react-router'

import type { MyFamilyAccount } from '@/api/family'

export function PaymentFailureBanner({
  paymentFailure,
}: {
  paymentFailure: NonNullable<MyFamilyAccount['paymentFailure']> | null
}) {
  const navigate = useNavigate()
  if (!paymentFailure) {
    return null
  }
  const message = paymentFailure.lockedOut
    ? 'Ordering is paused — a payment has been failing for over 13 days. Update your card or add funds to resume ordering; it re-enables automatically once fixed.'
    : 'A payment didn’t go through. Update your card or add funds — auto-orders resume as soon as it’s fixed.'
  return (
    <div
      data-testid="payment-failure-banner"
      className={
        paymentFailure.lockedOut
          ? 'flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-300/60 bg-red-50 px-4 py-3 dark:border-red-500/40 dark:bg-red-950/30'
          : 'flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 dark:border-amber-500/40 dark:bg-amber-950/30'
      }>
      <p className="text-sm font-medium text-foreground">{message}</p>
      <Button variant="default" size="sm" onClick={() => navigate('/billing')}>
        Fix payment
      </Button>
    </div>
  )
}
